import { Barn, Horse, Person } from "../../models";
import { getPersonByPersonId } from "../person/Person";
import { formatDisplayNameForUser } from "../person/PersonNameFormat";
import { getHorsesByBarnIdWithOnlyBasicDetails, getHorsesByPersonIdWithOnlyBasicDetails } from "./Horse";

export interface formattedOption {
    value: string; //Horse's id
    label: string; //Used for filtering options as a user types
    horseName: string; //Horse's Show Name - will display when item is selected
    horseNameLabel?: string; //Horse's Show Name + Nickname - will display in options
    barnLabel?: string; //Barn Name - will display in options
    ownerLabel?: string; //Date the horse was created - will display in options
    createdByLabel?: string;
    horse?: Horse;
}

export interface formattedGroupOfOptions {
    label: string;
    options: formattedOption[];
}

export async function getHorsesCreatedByUserOrInCurrentBarn(user: Person, currentBarn?: (Barn | null | undefined), listOfHorsesCreatedByUser?: (formattedOption[] | undefined)) {
    // First, get horses by User (check if horses created by the user were already passed in)
    let horsesCreatedByUser: (formattedOption[] | undefined) = listOfHorsesCreatedByUser || [];
    if (horsesCreatedByUser.length === 0 && user) {
        horsesCreatedByUser = await getHorsesCreatedByUser(user);
    }

    // Next, get horses by Barn
    let horsesByBarn: (formattedOption[] | undefined) = [];
    if (currentBarn != null && currentBarn != undefined) {
        horsesByBarn = await getHorsesInCurrentBarn(currentBarn);
    }

    // Next, remove any duplicates by taking barn horses out of the list of created by horses
    const horsesCreatedByUserUpdatedList = await removeBarnHorsesFromCreatedByGroup(horsesCreatedByUser, horsesByBarn);

    const result = {
        "createdBy": horsesCreatedByUserUpdatedList,
        "barn": horsesByBarn
    };

    return result;
};

export async function getHorsesCreatedByUser(user: Person) {
    try {
        let formattedList: formattedOption[] = [];
        const queryHorsesByCreatedByUser = await getHorsesByPersonIdWithOnlyBasicDetails(user.id);
        if (queryHorsesByCreatedByUser.isSuccess) {
            const result: Horse[] = queryHorsesByCreatedByUser.result;
            formattedList = await formatHorses(result, user);
        }
        return formattedList;
    } catch (error) {
        console.error("Error fetching horses created by user:", error);
        return [];
    }
};

export async function getHorsesInCurrentBarn(currentBarn: Barn) {
    try {
        let formattedList: formattedOption[] = [];
        if (currentBarn) {
            const queryHorsesByBarn = await getHorsesByBarnIdWithOnlyBasicDetails(currentBarn.id);
            if (queryHorsesByBarn.isSuccess) {
                const result: Horse[] = queryHorsesByBarn.result;
                formattedList = await formatHorses(result);
            }
        }
        return formattedList;
    } catch (error) {
        console.error("Error fetching barn horses:", error);
    }
};

export async function removeBarnHorsesFromCreatedByGroup(createdByList?: (formattedOption[] | null), barnHorsesList?: (formattedOption[] | null)) {
    let result: formattedOption[] = createdByList || [];
    // Only need to handle this if both of the lists have items in them
    if (createdByList && createdByList.length > 0 && barnHorsesList && barnHorsesList.length > 0) {
        // Remove all of the barnHorses from the CreatedBy list
        result = createdByList?.filter((createdByHorse) => !barnHorsesList.some((barnHorse) => barnHorse.value === createdByHorse.value));
    }
    return result;
};

// FORMATTERS
export async function formatHorses(horseArray: Horse[], user?: Person) {
    let formattedHorses = [];

    for (var i = 0; i < horseArray.length; i++) {
        const horse = horseArray[i];
        let object: formattedOption = await formatSingleHorse(horse, user);
        formattedHorses.push(object);
    }

    return formattedHorses;
};

export async function formatSingleHorse(horse: Horse, user?: Person) {
    const value = horse.id;
    const name = horse.name;
    const nickname = horse.nickname || "";
    const horseNameLabel = (name || "error") + (nickname ? " (" + nickname + ")" : "");
    const barnName = horse.barn?.name || horse.barnName || "unknown";
    const ownerLabel = horse.ownerName || horse.owner?.name || "unknown";

    let createdByLabel = "unknown";
    
    // If the horse is in the CreatedBy group, then we know the user who created it.
    // Saves us from querying the DB any more times than needed
    if (user) {
        createdByLabel = formatDisplayNameForUser(user);
    }
    
    // If we only have the personId, then we will make the DB query to get the createdBy person's name
    if (createdByLabel == "unknown" && horse.personId) {
        const personResult = await getPersonByPersonId(horse.personId);
        if (personResult.isSuccess) {
            const person: Person = personResult.result;
            createdByLabel = formatDisplayNameForUser(person);
        }
    }

    let object: formattedOption = {
        value: value,
        label: horseNameLabel + " " + barnName,
        horseName: name,
        horseNameLabel: horseNameLabel,
        barnLabel: barnName,
        ownerLabel: ownerLabel,
        createdByLabel: createdByLabel,
        horse: horse,
    };
    return object;
};